import { useEffect, useRef, useState, type ReactNode } from 'react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { useT } from '@/lib/i18n/useT';

/**
 * Pedir un texto corto. **Reemplaza a `window.prompt`.**
 *
 * El diálogo nativo del navegador rompe el producto en el peor momento: sale
 * con la tipografía del sistema, el botón del idioma del navegador y un
 * «localhost:5272 dice» encima. Da igual lo cuidada que esté la pantalla de
 * detrás — durante ese segundo el producto parece de otro.
 *
 * Hermano de `ConfirmDialog`, y con la misma forma: **controlado**, el que
 * llama es dueño del estado abierto y del ciclo de la mutación. `loading`
 * bloquea el botón mientras se guarda.
 *
 * El valor se lleva dentro a propósito: quien llama sólo quiere el texto final
 * en `onSubmit`, no seguir cada tecla.
 */
export interface PromptDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  /** Marca de agua del campo. Ayuda más que un ejemplo dentro del título. */
  placeholder?: string;
  /** Con qué llega el campo relleno. Vacío = en blanco. */
  defaultValue?: string;
  submitLabel?: string;
  cancelLabel?: string;
  loading?: boolean;
  /** Sólo se llama con un valor no vacío: el botón se bloquea si no lo hay. */
  onSubmit: (value: string) => void;
}

export function PromptDialog({
  open,
  onOpenChange,
  title,
  description,
  placeholder,
  defaultValue = '',
  submitLabel,
  cancelLabel,
  loading = false,
  onSubmit,
}: PromptDialogProps) {
  const t = useT();
  const [valor, setValor] = useState(defaultValue);
  const campo = useRef<HTMLInputElement>(null);

  /*
   * Al abrir: valor de partida y foco en el campo.
   *
   * El foco no es un detalle. El diálogo nativo lo daba, y sin él hay que
   * apuntar y hacer clic para escribir algo que ya se sabe — en un flujo que
   * es «pulsar, teclear un nombre, aceptar».
   */
  useEffect(() => {
    if (!open) return;
    setValor(defaultValue);
    const id = window.setTimeout(() => campo.current?.select(), 30);
    return () => window.clearTimeout(id);
  }, [open, defaultValue]);

  const limpio = valor.trim();

  const enviar = () => {
    if (!limpio || loading) return;
    onSubmit(limpio);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>

        <Input
          ref={campo}
          value={valor}
          onChange={(e) => setValor(e.target.value)}
          {...(placeholder ? { placeholder } : {})}
          // Enter envía, como en el diálogo nativo: es el gesto que ya tiene
          // aprendido quien escribe un nombre y quiere seguir.
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              enviar();
            }
          }}
          disabled={loading}
        />

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={loading}>
            {cancelLabel ?? t('common.cancel')}
          </Button>
          <Button onClick={enviar} loading={loading} disabled={!limpio}>
            {submitLabel ?? t('common.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
